import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as InfoIcon } from "assets/images/icons/icn_table_cell_Info.svg";

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ModalContainer from "components/ModalContainer";
import InputField from "components/InputField";
import { MenuSelect } from "components/Select/menuSelect";
import { OutlineButton } from "components/buttons/OutlineButton";
import { RadioButton } from "components/buttons/RadioButton";
import { SolidButton } from "components/buttons/SolidButton";

import ScheduleTimeField from "./components/ScheduleTimeField";
import { dayOfWeekOptions, defaultScheduleProfile, scheduleProfileType } from "./util/const";
import Form from "./util/form";

const MINUTES_PER_DAY = 24 * 60;
const TIMELINE_SIDE_PADDING = 36;
const scheduleGridColumns = "130px 200px 124px 18px 124px minmax(0, 1fr)";
const tooltipIconStyle = {
  width: "20px",
  height: "20px",
  cursor: "default",
};

const timeToMinutes = (time) => {
  const [hour = 0, minute = 0] = String(time || "00:00").split(":").map(Number);

  return (hour * 60) + minute;
};

const formatDisplayTime = (time, timeFormat) => {
  if (timeFormat === "24h") return time;
  if (time === "24:00") return "12:00 AM";

  const minutes = timeToMinutes(time);
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
};

const getSegments = (day) => {
  if (!day.enabled) return [];

  const from = timeToMinutes(day.from);
  const to = timeToMinutes(day.to);

  if (from === to) return [{ start: 0, end: MINUTES_PER_DAY }];

  if (from < to) return [{ start: from, end: to }];

  return [
    { start: from, end: MINUTES_PER_DAY },
    { start: 0, end: to },
  ];
};

const getWiredDays = (from, to, enabled, startTime, endTime) => {
  const start = dayOfWeekOptions.findIndex(({ key }) => key === from);
  const end = dayOfWeekOptions.findIndex(({ key }) => key === to);
  return dayOfWeekOptions.map((option, index) => {
    let inRange = false;
    if (enabled) {
      for (let offset = 0; offset < dayOfWeekOptions.length; offset += 1) {
        if ((start + offset) % dayOfWeekOptions.length === index) inRange = true;
        if ((start + offset) % dayOfWeekOptions.length === end) break;
      }
    }
    return { dayOfWeek: option.key, enabled: inRange, from: startTime, to: endTime };
  });
};

const getWiredRange = (days) => {
  const enabledDays = new Set((days || []).filter((day) => day.enabled).map((day) => day.dayOfWeek));
  if (enabledDays.size === 7) return { allWeek: true, from: "monday", to: "sunday" };
  const start = dayOfWeekOptions.find(({ key }, index) => (
    enabledDays.has(key) && !enabledDays.has(dayOfWeekOptions[(index + 6) % 7].key)
  ));
  const end = [...dayOfWeekOptions].reverse().find(({ key }, reverseIndex) => {
    const index = 6 - reverseIndex;
    return enabledDays.has(key) && !enabledDays.has(dayOfWeekOptions[(index + 1) % 7].key);
  });
  return { allWeek: false, from: start?.key || "monday", to: end?.key || start?.key || "friday" };
};

const Timeline = ({ day, timeFormat }) => {
  const theme = useTheme();
  const segments = getSegments(day);

  return (
    <Box sx={{ height: "30px", minWidth: 0, width: "100%", px: `${TIMELINE_SIDE_PADDING}px`, overflow: "hidden" }}>
      <Box sx={{ position: "relative", height: "30px" }}>
        <Box
          sx={{
            position: "absolute",
            top: "14px",
            left: 0,
            right: 0,
            height: "4px",
            backgroundColor: theme.palette.primary.greyThread,
          }}
        />
        {segments.map((segment, index) => {
          const left = (segment.start / MINUTES_PER_DAY) * 100;
          const width = ((segment.end - segment.start) / MINUTES_PER_DAY) * 100;

          return (
            <Box
              key={`${segment.start}-${segment.end}-${index}`}
              sx={{
                position: "absolute",
                top: "14px",
                left: `${left}%`,
                width: `${width}%`,
                height: "4px",
                backgroundColor: theme.palette.primary.greenPrimary,
              }}
            />
          );
        })}
        <Typography
          sx={{
            position: "absolute",
            top: 0,
            left: `${(timeToMinutes(day.from) / MINUTES_PER_DAY) * 100}%`,
            transform: "translateX(-50%)",
            fontSize: "10px",
            lineHeight: "12px",
            whiteSpace: "nowrap",
          }}
        >
          {formatDisplayTime(day.from, timeFormat)}
        </Typography>
        <Typography
          sx={{
            position: "absolute",
            top: "19px",
            left: `${(timeToMinutes(day.to) / MINUTES_PER_DAY) * 100}%`,
            transform: "translateX(-100%)",
            fontSize: "10px",
            lineHeight: "12px",
            whiteSpace: "nowrap",
          }}
        >
          {formatDisplayTime(day.to, timeFormat)}
        </Typography>
      </Box>
    </Box>
  );
};

const ScheduleModal = ({
  open,
  mode,
  profile,
  showEditGeneral = true,
  onClose,
  onSubmit,
}) => {
  const theme = useTheme();
  const form = Form.init(theme);
  const isCreateMode = mode === "create";
  const [timeFormat, setTimeFormat] = useState("ampm");
  const [formValue, setFormValue] = useState(structuredClone(defaultScheduleProfile));
  const [wiredRange, setWiredRange] = useState({ allWeek: true, from: "monday", to: "sunday" });
  const [wiredFrom, setWiredFrom] = useState("00:00");
  const [wiredTo, setWiredTo] = useState("24:00");
  const [isNameTouched, setIsNameTouched] = useState(false);

  const isNameInvalid = !formValue.name?.trim();
  const isInvalidWirelessTimeRange = formValue.profileType === scheduleProfileType.wireless
    && formValue.days.some((day) => (
      day.enabled && timeToMinutes(day.to) <= timeToMinutes(day.from)
    ));
  const isInvalidWiredTimeRange = formValue.profileType === scheduleProfileType.wired
    && !wiredRange.allWeek
    && timeToMinutes(wiredTo) <= timeToMinutes(wiredFrom);
  const isInvalid = isNameInvalid || isInvalidWirelessTimeRange || isInvalidWiredTimeRange;
  const showNameError = isNameTouched && isNameInvalid;

  const updateDay = (dayOfWeek, patch) => {
    setFormValue((prev) => ({
      ...prev,
      days: prev.days.map((day) => (
        day.dayOfWeek === dayOfWeek
          ? { ...day, ...patch }
          : day
      )),
    }));
  };

  const handleSubmit = () => {
    if (isInvalid) return;
    const payload = structuredClone(formValue);
    if (payload.profileType === scheduleProfileType.wired) {
      payload.days = wiredRange.allWeek
        ? getWiredDays("monday", "sunday", true, "00:00", "24:00")
        : getWiredDays(wiredRange.from, wiredRange.to, true, wiredFrom, wiredTo);
    }
    onSubmit(payload);
  };

  useEffect(() => {
    if (!open) return;

    setWiredRange({ allWeek: true, from: "monday", to: "sunday" });
    setWiredFrom("00:00");
    setWiredTo("24:00");

    if (profile) {
      setFormValue(structuredClone(profile));
      if ((profile.profileType || scheduleProfileType.wireless) === scheduleProfileType.wired) {
        const range = getWiredRange(profile.days);
        setWiredRange(range);
        const activeDay = (profile.days || []).find((day) => day.enabled);
        setWiredFrom(range.allWeek ? "00:00" : activeDay?.from || "00:00");
        setWiredTo(range.allWeek ? "24:00" : activeDay?.to || "24:00");
      }
    } else {
      setFormValue(structuredClone(defaultScheduleProfile));
      setWiredRange({ allWeek: true, from: "monday", to: "sunday" });
      setWiredFrom("00:00");
      setWiredTo("24:00");
    }

    setTimeFormat("ampm");
    setIsNameTouched(false);
  }, [open, profile?.id, mode]);

  return (
    <ModalContainer display={open} modalWidth="1250px">
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "26px" }}>
        <Typography
          sx={form.getHeaderTextStyle()}
        >
          {isCreateMode ? "Create" : "Edit"} Schedule Profile
        </Typography>

        <Button
          onClick={onClose}
          sx={{
            minWidth: 0,
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": { backgroundColor: theme.palette.primary.transparent },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr", alignItems: "center", mb: 2 }}>
        <Typography sx={{ ...form.getLabelStyle("150px"), pl: "30px" }}>Type</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "28px" }}>
          <RadioButton label="Wireless" value={scheduleProfileType.wireless} checked={formValue.profileType === scheduleProfileType.wireless} onChange={() => setFormValue((prev) => ({ ...prev, profileType: scheduleProfileType.wireless }))} name="schedule-profile-type" />
          <RadioButton label="Wired" value={scheduleProfileType.wired} checked={formValue.profileType === scheduleProfileType.wired} onChange={() => setFormValue((prev) => ({ ...prev, profileType: scheduleProfileType.wired }))} name="schedule-profile-type" />
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "28px" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "150px 300px", alignItems: "center" }}>
          <Typography sx={{ ...form.getLabelStyle("150px"), pl: "30px" }}>Schedule Name</Typography>
          <Box sx={{ width: "300px" }}>
            <InputField
              value={formValue.name}
              isValid={!showNameError}
              onChange={(value) => {
                setIsNameTouched(true);
                setFormValue((prev) => ({ ...prev, name: value }));
              }}
            />
          </Box>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={timeFormat}
          onChange={(_, value) => value && setTimeFormat(value)}
          sx={{
            width: "200px", height: "36px", padding: "3px",
            border: `1px solid ${theme.palette.primary.borderGreyPrimary}`,
            borderRadius: "8px", backgroundColor: theme.palette.primary.white,
            "& .MuiToggleButton-root": { flex: 1, height: "28px", border: 0, borderRadius: "6px", color: theme.palette.primary.greyPrimary, fontSize: theme.typography.customFontSize[1], fontWeight: theme.typography.customFontWeight[0], textTransform: "none", "&.Mui-selected": { color: theme.palette.primary.main, backgroundColor: "#DDE2FF", fontWeight: theme.typography.customFontWeight[2] }, "&.Mui-selected:hover": { backgroundColor: "#DDE2FF" }, "&:hover": { backgroundColor: theme.palette.primary.whiteSecound }, "&:not(:first-of-type)": { marginLeft: 0 } },
          }}
        >
          <ToggleButton value="ampm">AM/PM</ToggleButton>
          <ToggleButton value="24h">24 Hrs</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {formValue.profileType === scheduleProfileType.wireless ? <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: scheduleGridColumns,
            alignItems: "center",
            columnGap: "8px",
            borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
            pb: "12px",
          }}
        >
          <Typography sx={{ ...form.getColumnHeaderStyle(), ml: 3 }}>Day of Week</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Typography sx={form.getColumnHeaderStyle()}>Status</Typography>
          </Box>
          <Typography sx={form.getColumnHeaderStyle()}>From</Typography>
          <Box />
          <Typography sx={form.getColumnHeaderStyle()}>To</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Typography sx={form.getColumnHeaderStyle()}>Timeline</Typography>
            <Tooltip arrow title="In the timeline view, gray indicates that a feature is disabled, while green indicates that a feature is enabled.">
              <InfoIcon style={tooltipIconStyle} />
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ overflow: "hidden" }}>
          {dayOfWeekOptions.map((option, index) => {
            const day = formValue.days.find((item) => item.dayOfWeek === option.key) || {
              dayOfWeek: option.key,
              enabled: true,
              from: "00:00",
              to: "24:00",
            };
            const hasInvalidTimeRange = day.enabled
              && timeToMinutes(day.to) <= timeToMinutes(day.from);

            return (
              <Box
                key={option.key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: scheduleGridColumns,
                  alignItems: "center",
                  columnGap: "8px",
                  minHeight: "60px",
                  px: "0",
                  backgroundColor: index % 2 === 1 ? theme.palette.primary.whiteSecound : theme.palette.primary.white,
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[2],
                }}
              >
                <Box sx={{ ...form.getLabelStyle("130px"), ml: 3 }}>{option.label}</Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <RadioButton
                    label="Enable"
                    value={true}
                    checked={day.enabled === true}
                    onChange={() => updateDay(option.key, { enabled: true })}
                    name={`${option.key}-availability`}
                    sx={{ m: 0 }}
                  />
                  <RadioButton
                    label="Disable"
                    value={false}
                    checked={day.enabled === false}
                    onChange={() => updateDay(option.key, { enabled: false })}
                    name={`${option.key}-availability`}
                    sx={{ m: 0 }}
                  />
                </Box>
                <ScheduleTimeField
                  value={day.from}
                  disabled={!day.enabled}
                  timeFormat={timeFormat}
                  sx={form.getTextfieldStyle(!hasInvalidTimeRange, "114px")}
                  onChange={(value) => updateDay(option.key, { from: value })}
                />
                <Typography sx={{ textAlign: "center", ml: -1 }}>-</Typography>
                <ScheduleTimeField
                  value={day.to}
                  disabled={!day.enabled}
                  timeFormat={timeFormat}
                  allowEndOfDay
                  sx={form.getTextfieldStyle(!hasInvalidTimeRange, "110px")}
                  onChange={(value) => updateDay(option.key, { to: value })}
                />
                <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                  <Timeline day={day} timeFormat={timeFormat} />
                </Box>
              </Box>
            );
          })}
        </Box>
      </> : <Box sx={{ mt: 3 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr", alignItems: "center", mb: 2 }}>
          <Typography sx={{ ...form.getColumnHeaderStyle(), pl: "30px" }}>Day of Week</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
            <RadioButton label="All Week" value="all" checked={wiredRange.allWeek} onChange={() => {
              setWiredRange({ allWeek: true, from: "monday", to: "sunday" });
              setWiredFrom("00:00");
              setWiredTo("24:00");
            }} name="wired-day-mode" />
            <RadioButton label="Select Day(s)" value="select" checked={!wiredRange.allWeek} onChange={() => setWiredRange((prev) => ({ ...prev, allWeek: false }))} name="wired-day-mode" />
          </Box>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "150px 150px 20px 150px minmax(0, 1fr)", alignItems: "end", columnGap: 1, mb: 1 }}>
          <Box />
          <Typography sx={form.getColumnHeaderStyle()}>From</Typography>
          <Box />
          <Typography sx={form.getColumnHeaderStyle()}>To</Typography>
          <Box />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "150px 150px 20px 150px minmax(0, 1fr)", alignItems: "center", columnGap: 1, pb: 2 }}>
          <Box />
          <MenuSelect
            value={wiredRange.from}
            onChange={(option) => setWiredRange((prev) => ({ ...prev, from: option.value }))}
            options={dayOfWeekOptions.map((day) => ({ value: day.key, label: day.label }))}
            width="100%"
            controlHeight="42px"
            stepArrowWidth={40}
            disabled={wiredRange.allWeek}
            showStepArrows
          />
          <Typography sx={{ textAlign: "center" }}>-</Typography>
          <MenuSelect
            value={wiredRange.to}
            onChange={(option) => setWiredRange((prev) => ({ ...prev, to: option.value }))}
            options={dayOfWeekOptions.map((day) => ({ value: day.key, label: day.label }))}
            width="100%"
            controlHeight="42px"
            stepArrowWidth={40}
            disabled={wiredRange.allWeek}
            showStepArrows
          />
          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1, ml: 1 }}>{dayOfWeekOptions.map((day) => {
            const active = wiredRange.allWeek || getWiredDays(wiredRange.from, wiredRange.to, true, wiredFrom, wiredTo).find((item) => item.dayOfWeek === day.key)?.enabled;
            return <Box key={day.key} sx={{ minWidth: 38, textAlign: "center", px: 1, py: .7, borderRadius: 5, color: active ? "common.white" : "text.secondary", bgcolor: active ? "primary.main" : "transparent", border: active ? "1px solid transparent" : `1px solid ${theme.palette.primary.greyThread}`, fontSize: 13 }}>{day.shortLabel.slice(0, 1) + day.shortLabel.slice(1).toLowerCase()}</Box>;
          })}</Box>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "150px 150px 20px 150px minmax(0, 1fr)", alignItems: "center", columnGap: 1, mt: 2, pb: 2 }}>
          <Typography sx={{ ...form.getColumnHeaderStyle(), pl: "30px" }}>Timeline</Typography>
          <Typography sx={form.getColumnHeaderStyle()}>From</Typography>
          <Box />
          <Typography sx={form.getColumnHeaderStyle()}>To</Typography>
          <Box />
          <Box />
          <ScheduleTimeField
            value={wiredFrom}
            disabled={wiredRange.allWeek}
            timeFormat={timeFormat}
            controlWidth="40px"
            sx={form.getTextfieldStyle(!isInvalidWiredTimeRange, "100%")}
            onChange={setWiredFrom}
          />
          <Typography>-</Typography>
          <ScheduleTimeField
            value={wiredTo}
            disabled={wiredRange.allWeek}
            timeFormat={timeFormat}
            controlWidth="40px"
            allowEndOfDay
            sx={form.getTextfieldStyle(!isInvalidWiredTimeRange, "100%")}
            onChange={setWiredTo}
          />
          <Timeline day={{ enabled: true, from: wiredFrom, to: wiredTo }} timeFormat={timeFormat} />
        </Box>
      </Box>}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "20px",
          mt: "30px",
          pt: "24px",
          borderTop: formValue.profileType === scheduleProfileType.wireless ? `1px solid ${theme.palette.primary.greyThread}` : "none",
        }}
      >
        <OutlineButton width="100px" height="32px" onClick={onClose}>
          Cancel
        </OutlineButton>
        <SolidButton width="100px" height="32px" disabled={isInvalid || (!isCreateMode && !showEditGeneral)} onClick={handleSubmit}>
          {isCreateMode ? "Create" : "Save"}
        </SolidButton>
      </Box>
    </ModalContainer>
  );
};

export default ScheduleModal;
